import { api } from "../config/api";
import { post } from "../api";
import { isSysadminRole } from "../helpers/roleUtils";

export { normalizeRoleKey } from "../helpers/roleUtils";

export interface FeedbackSessionArgs {
  business_id: string | number | null;
  device_token: string | null;
  session_id: string | null;
}

export function isSysadminFeedbackRole(role: string | null | undefined): boolean {
  return isSysadminRole(role);
}

export function getTicketsSessionForRole(
  session: FeedbackSessionArgs,
  userRole: string | null | undefined
): FeedbackSessionArgs {
  if (isSysadminFeedbackRole(userRole)) {
    return { ...session, business_id: null };
  }
  return session;
}

export interface TicketApiItem {
  ticket_id?: string | number;
  id?: string | number;
  case_id?: string | number;
  business_id?: string | number;
  business_name?: string;
  subject?: string;
  description?: string;
  severity?: string;
  status?: string;
  created?: string;
  created_at?: string;
  user?: string;
  email?: string;
  attachment?: string;
  attachments?: unknown;
  ticket_communication?: unknown;
}

export interface GetTicketsResponse {
  success?: boolean;
  data?: { tickets?: TicketApiItem[] };
  errors?: string;
}

export interface GetSingleTicketDetailsPayload extends FeedbackSessionArgs {
  ticket_id: string | number;
}

export interface GetSingleTicketDetailsResponse {
  success?: boolean;
  data?: {
    tickets?: TicketApiItem[];
    ticket_info?: TicketApiItem;
    ticket?: TicketApiItem;
    ticket_id?: string | number;
    [key: string]: unknown;
  };
  ticket_info?: TicketApiItem;
  ticket?: TicketApiItem;
  errors?: string;
}

export interface GetUploadInfoPayload {
  doc_type: string;
  doc_name: string;
  doc_extra_info?: string;
  device_token: string | null;
  session_id: string | null;
  business_id: string | number | null;
  client_id?: string | number;
}

export interface GetUploadInfoResponse {
  success?: boolean;
  data?: {
    upload_info?: { url: string; fields: Record<string, string>; uuid: string };
  };
  errors?: string;
}

export interface CreateTicketPayload extends FeedbackSessionArgs {
  subject: string;
  description: string;
  category?: string;
  ticket_priority?: string;
  status?: string;
  severity: string;
  uuid?: string;
  attachment?: string;
  created_at?: string;
}

export interface CreateTicketResponse {
  success?: boolean;
  errors?: string;
}

export interface CreateCommunicationPayload {
  ticket_id: number | string;
  message: string;
  is_active: boolean;
  device_token: string | null;
  session_id: string | null;
  business_id: string | number;
}

export interface CreateCommunicationMsgInfo {
  communication_id?: number;
  ticket_id?: number;
  user?: string;
  message?: string;
  is_active?: boolean;
  created_at?: string;
}

export interface CreateCommunicationResponse {
  success?: boolean;
  message?: string;
  data?: {
    created_at?: string;
    created?: string;
    msg_info?: CreateCommunicationMsgInfo;
  };
  meta?: {
    input?: {
      communication_id?: number | null;
      ticket_id?: number;
      username?: string;
      message?: string;
      is_active?: boolean;
    };
  };
  errors?: unknown;
  error_code?: string | null;
}

export interface GetDownloadInfoPayload extends FeedbackSessionArgs {
  doc_file_path: string;
}

export interface GetDownloadInfoResponse {
  success?: boolean;
  data?: { download_info?: string };
  errors?: string;
}

export interface UpdateTicketSeverityPayload extends FeedbackSessionArgs {
  ticket_id: string | number;
  severity: string;
}

export interface UpdateTicketSeverityResponse {
  success?: boolean;
  errors?: string;
}

export const UPDATE_TICKET_STATUS_DEFAULT_COMMENT = "static for now";

export interface UpdateTicketStatusPayload extends FeedbackSessionArgs {
  ticket_id: string | number;
  comment: string;
  previous_status: string;
  new_status: string;
}

export interface UpdateTicketStatusStatusInfo {
  ticketstatus_id?: number;
  comment?: string;
  previous_status?: string;
  new_status?: string;
  user?: string;
  ticket_id?: number;
  ticket_updated?: boolean;
}

export interface UpdateTicketStatusResponse {
  success?: boolean;
  data?: {
    status_info?: UpdateTicketStatusStatusInfo;
  };
  meta?: {
    input?: {
      ticket_id?: number;
      comment?: string;
      previous_status?: string;
      new_status?: string;
      business_id?: string | number | null;
      username?: string;
    };
  };
  errors?: unknown;
  error_code?: string | null;
}

function getTickets(session: FeedbackSessionArgs): Promise<GetTicketsResponse> {
  return post<GetTicketsResponse>(api.GET_TICKETS, session);
}

function getSingleTicketDetails(
  payload: GetSingleTicketDetailsPayload
): Promise<GetSingleTicketDetailsResponse> {
  return post<GetSingleTicketDetailsResponse>(api.GET_SINGLE_TICKET_DETAILS, payload);
}

function getUploadInfo(payload: GetUploadInfoPayload): Promise<GetUploadInfoResponse> {
  return post<GetUploadInfoResponse>(api.GET_UPLOAD_INFO, payload);
}

function createTicket(payload: CreateTicketPayload): Promise<CreateTicketResponse> {
  return post<CreateTicketResponse>(api.CREATE_TICKET, payload);
}

function createCommunication(
  payload: CreateCommunicationPayload
): Promise<CreateCommunicationResponse> {
  return post<CreateCommunicationResponse>(api.CREATE_COMMUNICATION, payload);
}

function getDownloadInfo(payload: GetDownloadInfoPayload): Promise<GetDownloadInfoResponse> {
  return post<GetDownloadInfoResponse>(api.GET_DOWNLOAD_INFO, payload);
}

function updateTicketSeverity(
  payload: UpdateTicketSeverityPayload
): Promise<UpdateTicketSeverityResponse> {
  return post<UpdateTicketSeverityResponse>(api.UPDATE_TICKET_SEVERITY, payload);
}

function updateTicketStatus(
  payload: UpdateTicketStatusPayload
): Promise<UpdateTicketStatusResponse> {
  return post<UpdateTicketStatusResponse>(api.UPDATE_TICKET_STATUS, payload);
}

export const feedbackService = {
  getTickets,
  getSingleTicketDetails,
  getUploadInfo,
  createTicket,
  createCommunication,
  getDownloadInfo,
  updateTicketSeverity,
  updateTicketStatus,
};
